import React, { useEffect, useState, useCallback } from "react";
import {
  FaHandPointRight,
  FaPhone,
  FaWhatsapp,
  FaPlayCircle,
} from "react-icons/fa";
import apiInstance from "../utils/axios";
import moment from "moment";
import SEO from "./SEO";

const ANDROID_64_BIT_URL = "https://www.appnew.cloud/mb64.apk";
const ANDROID_32_BIT_URL = "https://www.appnew.cloud/mb64.apk";
const IOS_APP_STORE_URL = "https://www.appnew.cloud/mb64.apk"; // Replace with actual App Store link
const DEFAULT_DOWNLOAD_URL = ANDROID_64_BIT_URL; // default to 64-bit

function Hero() {
  const [games, setGames] = useState([]);
  const [declaredResults, setDeclaredResults] = useState([]);
  const [whatsapp, setWhatsapp] = useState("");
  const [mobile, setMobile] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(DEFAULT_DOWNLOAD_URL);
  const today = moment().format("YYYY-MM-DD");

  // --- STATIC GAME RATES ---
  // Game rates are now hardcoded here instead of being fetched from an API.
  const gameRatesArray = [
  { rateLabel: "Single Digit", rate: 10, value: 100 },
  { rateLabel: "Jodi Digit", rate: 10, value: 1000 },
  { rateLabel: "Single Pana", rate: 10, value: 1500 },
  { rateLabel: "Double Pana", rate: 10, value: 3000 },
  { rateLabel: "Triple Pana", rate: 10, value: 7000 },
  { rateLabel: "Red Bracket", rate: 10, value: 1000 },
  { rateLabel: "Half Sangam A/B", rate: 10, value: 10000 },
  { rateLabel: "Full Sangam", rate: 10, value: 100000 },
];
  // Enhanced Device Architecture Detection
  const detectArchitecture = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    console.log("UserAgent =>", ua);

    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      setDownloadUrl(IOS_APP_STORE_URL);
      return;
    }

    if (/android/i.test(ua)) {
      // Check for ARM64 or x86_64 indicators
      if (/aarch64|armv8|arm64|x86_64/i.test(ua)) {
        setDownloadUrl(ANDROID_64_BIT_URL);
      } else if (/armv7|armeabi/i.test(ua)) {
        setDownloadUrl(ANDROID_32_BIT_URL);
      } else {
        // Unknown architecture, default to 64-bit as most devices are now 64-bit
        setDownloadUrl(ANDROID_64_BIT_URL);
      }
    } else {
      setDownloadUrl(DEFAULT_DOWNLOAD_URL); // For desktop or unknown OS
    }
  };

  useEffect(() => {
    detectArchitecture();
  }, []);

  const handleDownload = () => {
    if (downloadUrl) {
      window.location.href = downloadUrl;
    } else {
      alert("Could not find a suitable download link.");
    }
  };

  // Fetch WhatsApp & Mobile Info
  useEffect(() => {
    const fetchWhatsapp = async () => {
      try {
        const res = await apiInstance.get('/api/settings/general/whatsapp');
        setWhatsapp(res.data.whatsappnumber);
        setMobile(res.data.mobile);
      } catch (err) {
        console.log(err);
      }
    };
    fetchWhatsapp();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await apiInstance.get("/api/marketManagement/getMarketGames");
      const data = response.data || [];
      const activeGames = data.filter((game) => game.isActive);
      const currentDay = moment().format("dddd");

      const mappedGames = activeGames.map((game) => {
        const weekendRecord = game.weekends.find(
          (item) => item.day.toLowerCase() === currentDay.toLowerCase()
        );

        let status = game.is_open ? "Running" : "Closed for today";
        let displayOpenTime = game.openTime;
        let displayCloseTime = game.closeTime;
        const gameName = game.gameName || game.name || 'unknown-game';

        const cleanGameName = gameName.toString()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');

        if (weekendRecord) {
          if (!weekendRecord.is_on || !weekendRecord.is_open) {
            status = "Closed for today";
            displayOpenTime = weekendRecord.openTime;
            displayCloseTime = weekendRecord.closeTime;
          } else {
            status = "Running";
          }
        }

        return {
          name: game.gameName.toUpperCase(),
          originalName: game.gameName,
          openTime: displayOpenTime,
          closeTime: displayCloseTime,
          status,
          createdAt: game.createdAt,
          jodiChart: `/mrecords/${cleanGameName}-jodi-chart`,
          pannaChart: `/mrecords/${cleanGameName}-panel-chart`,
        };
      });

      const sortedGames = mappedGames.sort((a, b) =>
        moment(a.createdAt).diff(moment(b.createdAt))
      );
      setGames(sortedGames);
    } catch (error) {
      console.error("Error fetching games:", error);
    }
  };

  const fetchDeclaredResults = useCallback(async () => {
    try {
      const response = await apiInstance.get("/api/mainmarketdeclareResult/DeclareResult");
      const results = response.data.results || [];
      const todayResults = results.filter(
        (item) => item.date === today && item.marketName === "Main Market"
      );
      setDeclaredResults(todayResults);
    } catch (error) {
      console.error("Error fetching declared results:", error);
    }
  }, [today]);

  useEffect(() => {
    fetchGames();
  }, []);

  useEffect(() => {
    fetchDeclaredResults();
  }, [fetchDeclaredResults]);

  const getResultStringForGame = (gameName) => {
    const gameResults = declaredResults.filter(
      (item) =>
        item.gameName.trim().toUpperCase() === gameName.trim().toUpperCase()
    );
    const openResult = gameResults.find((item) => item.gameType === "open");
    const closeResult = gameResults.find((item) => item.gameType === "close");

    if (openResult && closeResult) {
      return `${openResult.panna}-${openResult.digit}${closeResult.digit}-${closeResult.panna}`;
    } else if (openResult && !closeResult) {
      return `${openResult.panna}-${openResult.digit}*-***`;
    } else if (!openResult && closeResult) {
      return `***-*${closeResult.digit}-${closeResult.panna}`;
    } else {
      return "***-**-***";
    }
  };

  return (
    <div>
      <SEO />
      <a href={`https://wa.me/+91${whatsapp}`} target="blank" className="whatsapp-icon-div">
        <FaWhatsapp size={26} color="white" />
      </a>

      <div className="flex flex-col items-center min-h-screen mt-[1cm] ">
        {/* Header Section */}
        <section className="flex flex-col w-full text-center header mt-4">
          <div className="z-10">
            <p className="text-[35px] sm:text-[40px] md:text-[55px] font-bold">
              Welcome to <span className="text-orange-500"> Matka</span>
            </p>
            <p className=" text-xl md:text-2xl">
              Business Of Faith, With Confidence
            </p>
          </div>
        </section>

        {/* Download Button Section */}
        <section id="hero" className="w-full h-72 bg-pink-200 pt-4">
          <div className="max-w-7xl mx-auto px-4 overflow-hidden sm:px-6 lg:px-8">
            <div className="flex justify-center space-x-6">
              <button onClick={handleDownload}
                className="animate-bounce bg-orange-500 p-2 rounded-full w-72 text-white border-white border-2 shadow mt-3 text-center ">
                <FaHandPointRight className="inline-block text-lg mr-2" />
                Download Now
              </button>
            </div>

            <div className="mt-8 text-center text-base">
              <h3 className="text-2xl text-gray-800">
                <strong> +91 {whatsapp} </strong>
              </h3>
            </div>

            <div className="mt-6 flex justify-center space-x-6">
              <a className="bg-white border-orange-500 p-3 rounded-full text-gray-800 w-48 border-2 shadow text-center"
                href={`tel:+91${mobile}`}>
                <FaPhone className="inline-block mr-2" />
                Call Now
              </a>

              <a className="bg-green-600 p-3 rounded-full text-white w-48 border-white border-2 shadow text-center"
                href={`https://wa.me/+91${whatsapp}`} target="_blank" rel="noopener noreferrer">
                <FaWhatsapp className="inline-block mr-2" />
                Whats App
              </a>
            </div>
          </div>
        </section>

        {/* Rates Section */}
        <section id="pricing" className="w-full h-auto pt-4 p-4 bg-gray-100">
          <div className="text-center my-4">
            <h2 className="text-4xl font-bold">
              Game<span className="text-[#15400e]"> Rates</span>
            </h2>
            <p className="separator">We have Best Game Rates for you</p>
            <br /><br />

            <div className="w-full grid gap-4 grid-cols-1 md:grid-cols-2">
              {gameRatesArray.map((m, i) => (
                <div key={i} className="flex items-center justify-between border-l-[5px] border-l-[#15400e] px-[.3cm] py-[.25cm] bg-white rounded-[5px]">
                  <div className="w-fit flex items-center gap-[.2cm]">
                    <FaHandPointRight size={20} />
                    <p className="font-bold text-[18px]">{m.rateLabel}</p>
                  </div>
                  <p className="font-bold text-[#15400e] text-[18px]">
                    {m.rate} RS KA {m.value} RS
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Available Games Section */}
        <section id="availableGames" className="w-full h-auto pt-4 p-4">
          <div className="text-center my-4">
            <h2 className="text-4xl font-bold">
              Available<span className="text-[#15400e]"> Games</span>
            </h2>
            <p className="separator">We have multiple types of games for you</p>
          </div>

          <div className="md:grid md:grid-cols-2 md:gap-4" id="available-game-row">
            {games.map((game, index) => (
              <div key={index} className="bg-white rounded-r-lg grid grid-cols-2 pt-2 h-36 text-lg md:text-lg shadow border-l-4 border-l-[#15400e] my-2 px-2">
                <div className="mt-4 text-center">
                  {/* Game ka naam bhi naye color mein */}
                  <h3 className="text-[#15400e]"><strong>{game.name}</strong></h3>

                  {/* Result string ka color badla gaya */}
                  <h2 className="text-[#15400e]">
                    <strong>{getResultStringForGame(game.name)} </strong>
                  </h2>

                  <div>
                    {/* Chart links ka color badla gaya */}
                    <a className="text-[#15400e] viewChartFont" href={`/jodi-chart/${game.name}`}>
                      {game.jodiChart ? "Jodi chart" : "View Chart"}
                    </a>
                    {game.pannaChart && (
                      <>
                        <span> | </span>
                        <a className="text-[#15400e] viewChartFont" href={`/panna-chart/${game.name}`}>
                          Panna Chart
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right pr-4 flex flex-col items-end ">
                  <button onClick={handleDownload} className="mr-2 ">
                    {/* Play button ke icon aur shadow ka color badla gaya */}
                    <FaPlayCircle size={60} className="text-[#15400e] text-2xl shadow rounded-full shadow-[#15400e] shadow-lg" />
                  </button>
                  <h5 className="mt-2 text-base font-bold">Play Now</h5>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Hero;